import React from 'react';
import Loadable from 'react-loadable';
import {
  AccessibilityAuditComponentResultsSection,
  ClassnamePartial,
  EnvironmentPartial,
  QaIdPartial,
  CodeSnippet,
  Link,
  List,
  PlatformTab,
  PropList,
  PropListItem,
  Section,
} from '../../../../components';
import V5Notice from '../../v5-notice/V5Notice';

const Text = Loadable({
  loader: () => import('@hudl/uniform-ui-components'),
  loading: () => null,
  render(loaded, props) {
    const Component = loaded.Text;
    return <Component {...props} />;
  },
});

const Button = Loadable({
  loader: () => import('@hudl/uniform-ui-components'),
  loading: () => null,
  render(loaded, props) {
    const Component = loaded.Button;
    return <Component {...props} />;
  },
});

const snippet = require('raw-loader!../../../../data/snippets/button.example');

const WebTab = () => {
  return (
    <PlatformTab>
      <V5Notice
        code
        componentName="Button"
        storybookUrl="https://uniform-web.storybook.hudltools.com/?path=/docs/general-buttons--docs#button"
      />
      <CodeSnippet
        scope={{ React: React, Button: Button }}
        code={snippet}
        platform="react"
        gitHubLink="buttons/button"
      />

      <Section title="Props">
        <PropList header="Visual">
          <PropListItem name="buttonStyle" types={['string']} isRequired>
            <Text>Determines the button style.</Text>
            <List type="unordered">
              <li>
                <code>standard</code> (default)
              </li>
              <li>
                <code>minimal</code>
              </li>
            </List>
          </PropListItem>

          <PropListItem isRequired name="buttonType" types={['string']}>
            <Text>Determines what type appears on the button.</Text>
            <List type="unordered">
              <li>
                <code>primary</code> (default)
              </li>
              <li>
                <code>secondary</code>
              </li>
              <li>
                <code>destroy</code>
              </li>
              <li>
                <code>confirm</code>
              </li>
              <li>
                <code>subtle</code>
              </li>
              <li>
                <code>cancel</code>
              </li>
            </List>
          </PropListItem>

          <PropListItem name="children" types={['ReactNode']}>
            <Text>Displays as the label of the button.</Text>
          </PropListItem>

          <ClassnamePartial componentName="button" />

          <PropListItem name="collapsePadding" types={['string']}>
            <Text>
              Collapses side padding of minimal buttons for better alignment.
            </Text>
            <List type="unordered">
              <li>
                <code>left</code>
              </li>
              <li>
                <code>right</code>
              </li>
              <li>
                <code>horizontal</code>
              </li>
            </List>
          </PropListItem>

          <EnvironmentPartial />

          <PropListItem name="icon" types={['ReactNode']}>
            <Text>
              Adds an icon element to the button. You can either pass an Icon
              component or your own icon element.
            </Text>
          </PropListItem>

          <PropListItem name="iconAlignment" types={['string']}>
            <Text>Determines what side of the label the icon displays.</Text>
            <List type="unordered">
              <li>
                <code>left</code> (default)
              </li>
              <li>
                <code>right</code>
              </li>
            </List>
          </PropListItem>

          <PropListItem name="isBlock" types={['boolean']}>
            <Text>
              Determines whether or not the width fills 100% of the button’s
              container.
            </Text>
          </PropListItem>

          <PropListItem name="isDisabled" types={['boolean']}>
            <Text>Determines whether the button is disabled.</Text>
          </PropListItem>

          <PropListItem name="size" types={['string']}>
            <Text>Determines the button’s size.</Text>
            <List type="unordered">
              <li>
                <code>xsmall</code>
              </li>
              <li>
                <code>small</code>
              </li>
              <li>
                <code>medium</code> (default)
              </li>
              <li>
                <code>large</code>
              </li>
            </List>
          </PropListItem>

          <PropListItem name="status" types={['string']}>
            <Text>Adds a status indicator to the button.</Text>
            <List type="unordered">
              <li>
                <code>spinning</code> - Adds a spinner next to the text.
              </li>
              <li>
                <code>success</code> - Makes the button green and adds a
                checkmark next to the text.
              </li>
              <li>
                <code>failure</code> - Makes the button red and adds an X next
                to the text.
              </li>
            </List>
          </PropListItem>

          <PropListItem name="overflowStyle" types={['string']}>
            <Text>
              Determines whether to truncate text that overflows the width of
              the button with an ellipsis or to wrap it to another line with a
              hyphen.
            </Text>
            <List type="unordered">
              <li>
                <code>ellipsis</code>
              </li>
              <li>
                <code>wrap</code> (default)
              </li>
            </List>
          </PropListItem>
        </PropList>

        <PropList header="Events">
          <PropListItem name="onClick" types={['(e?) => void']}>
            <Text>Responds to the button click event.</Text>
          </PropListItem>
        </PropList>

        <PropList header="Miscellaneous">
          <QaIdPartial componentName="button" />

          <PropListItem name="...props" types={[]}>
            <Text>
              You can pass in any of the other props from an HTML{' '}
              <Link href="https://developer.mozilla.org/en-US/docs/Web/HTML/Element/button">
                button
              </Link>{' '}
              element.
            </Text>
          </PropListItem>
        </PropList>
      </Section>

      <Section title="Notes">
        <List type="unordered">
          <li>
            If you include an icon but no text, your button is automatically
            styled as icon-only.
          </li>
          <li>
            A button may be wrapped in a{' '}
            <Link href="/component/link/design">link</Link> when the
            call-to-action takes the user elsewhere. Work with your designer to
            decide the best approach.
          </li>
        </List>
      </Section>

      <AccessibilityAuditComponentResultsSection
        platform="React"
        componentName="Button"
      />
    </PlatformTab>
  );
};

export default WebTab;
